(function() {
	'use strict';

	angular
		.module('assetCommon')
		.factory('assetService', assetService);

	function assetService($http, $q, fileUploadService, bucketService, cancellationService, jobService, promiseUtilityService, _, assetMapper, revisionMapper, shareMapper, facetMapper, dateTimeUtility, utility, uriUtility, httpUtility, assetCommonConfig) {
			var revisionRequestLimit = 1024;
			var mergeWithinMinutes = 5;

			var assetFieldsToRemove = '(file.(metadata.exiftool,creation),formats.(files.(metadata.exiftool,creation,sha1Hash),ops),revision.changes)';
			var assetFieldFilter = '!' + assetFieldsToRemove;
			var assetsFieldFilter = '!items.' + assetFieldsToRemove;
			var audioVideoFormats = {
				'audio/mpeg': { audioCodec: 'libmp3lame' },
				'audio/ogg': { audioCodec: 'libvorbis' },
				'video/mp4': { audioCodec: 'libfdk_aac', videoCodec: 'libx264' },
				'video/webm': { audioCodec: 'libvorbis', videoCodec: 'libvpx' }
			};

			function updateAssetAsync(assetId, collectChanges, cancellationToken, shareToken) {
				var ops = [];

				function updateOriginal(fileId) {
					// clearing original is not supported
					if (!fileId)
						return;

					ops.push({ op: 'setFile', fileId: fileId, update: true });
				}

				function updateSource(fileId) {
					var op = { op: 'setSource' };
					if (fileId)
						op.fileId = fileId;

					ops.push(op);
				}

				function createFormat(formatOptions) {
					if (formatOptions.asset.kind !== 'image' && formatOptions.asset.kind !== 'audio' && formatOptions.asset.kind !== 'video')
						throw 'Can only create formats for image, audio, or video assets.';

					var createFormatOperation = {
						op: 'createFormat',
						name: formatOptions.name,
						ops: []
					};

					if (formatOptions.format && formatOptions.format !== formatOptions.asset.file.mediaType) {
						if (formatOptions.asset.kind === 'video')
							createFormatOperation.ops.push({ op: 'video', codec: audioVideoFormats[formatOptions.format].videoCodec });

						if (formatOptions.asset.kind !== 'image')
							createFormatOperation.ops.push({ op: 'audio', codec: audioVideoFormats[formatOptions.format].audioCodec });

						createFormatOperation.ops.push({ op: 'convert', mediaType: formatOptions.format });
					}
					if (formatOptions.resize && (formatOptions.resize.width || formatOptions.resize.height))
						createFormatOperation.ops.push({ op: 'resize', width: formatOptions.resize.width, height: formatOptions.resize.height });

					if (createFormatOperation.ops.length) {

						// add a name operation as the last operation; see http://git/Logos/AssetDesk/blob/master/docs/FileOperations.md
						if (formatOptions.fileName)
							createFormatOperation.ops.push({ op: 'setName', value: formatOptions.fileName });

						ops.push(createFormatOperation);
					}
				}

				function removeFormats(fileIds) {
					(fileIds || [])
						.map(function (f) {
							return { op: 'removeFormat', fileId: f };
						})
						.forEach(function (op) {
							ops.push(op);
						});
				}

				var edit = {
					updateOriginal: updateOriginal,
					updateSource: updateSource,
					createFormat: createFormat,
					removeFormats: removeFormats
				};

				collectChanges(edit);

				return ops.length ?
					editAssetsAsync([assetId], ops, { shouldAwaitCompletion: true, cancellationToken: cancellationToken }, shareToken)
						.then(function (results) {
							return results[0];
						}) : promiseUtilityService.fromResult(null);
			}

			function createShareAsync(assetId, expirationDate) {
				return $http.post(assetCommonConfig.assetServiceBaseUri + 'shares', { assetId: assetId, expires: expirationDate })
					.then(function (response) {
						return shareMapper.map(response.data);
					});
			}

			function getShareAssetIdAsync(shareToken) {
				return $http.get(uriUtility.fromPattern(assetCommonConfig.assetServiceBaseUri + 'shares', { token: shareToken }))
					.then(function (response) {
						if (!response.data.items || response.data.items.length !== 1)
							return $q.reject({ status: 'notfound' });
						return response.data.items[0].assetId;
					})
					.catch(function () {
						return $q.reject({ status: 'error' });
					});
			}

			function getAssetUri(id, options) {
				options = options || {};
				return uriUtility.fromPattern(assetCommonConfig.assetServiceBaseUri + 'assets/{id}', { id: id, rev: options.revision, share: options.shareToken });
			}

			function getAssetExifToolAsync(id, fileId, cancellationToken) {
				return $http.get(uriUtility.fromPattern(assetCommonConfig.assetServiceBaseUri + 'assets/{id}/files/{fileId}/exiftool', { id: id, fileId: fileId }, { timeout: cancellationToken }))
					.then(function (response) {
						return response.data;
					})
					.then(function (response) {
						return jobService.awaitBulkJobCompletionAsync([response.id]);
					})
					.then(function (response) {
						return jobService.multiGetJobsAsync([response.jobs[0].id]);
					})
					.then(function (response) {
						return response.items[0].response.content;
					})
					.catch(function (error) {
						return $q.reject({
							status: error.status === 404 ? 'notfound' : 'error'
						});
					});
			}

			function addToFavoritesAsync(assetIds) {
				return doSimpleAssetOperationAsync(assetIds, 'addToFavorites');
			}

			function removeFromFavoritesAsync(assetIds) {
				return doSimpleAssetOperationAsync(assetIds, 'removeFromFavorites');
			}

			function deleteAssetsAsync(assetIds) {
				return doSimpleAssetOperationAsync(assetIds, 'deleteAsset');
			}

			function undeleteAssetsAsync(assetIds) {
				return doSimpleAssetOperationAsync(assetIds, 'undeleteAsset');
			}

			function doSimpleAssetOperationAsync(assetIds, op) {
				return editAssetsAsync(assetIds, [{ op: op }], { fireAndForget: true })
					.catch(function (error) {
						return $q.reject({
							status: error.status === 404 ? 'notfound' : 'error'
						});
					});
			}

			function addToBoardAsync(assetIds, boardId, cancellationToken) {
				return editAssetsAsync(assetIds, [{ op: 'addToBoard', boardId: boardId }], { cancellationToken: cancellationToken });
			}

			function removeFromBoardAsync(assetIds, boardId, cancellationToken) {
				return editAssetsAsync(assetIds, [{ op: 'removeFromBoard', boardId: boardId }], { cancellationToken: cancellationToken });
			}

			function getAssetRevisionsUri(id, options) {
				options = options || {};
				return uriUtility.fromPattern(assetCommonConfig.assetServiceBaseUri + 'assets/{id}/revisions', { id: id, limit: options.limit, next: options.next });
			}

			function getAssetAsync(id, options, cancellationToken) {
				return $http.get(uriUtility.fromPattern(getAssetUri(id, options), { fields: assetFieldFilter }), { timeout: cancellationToken })
					.then(function (response) {
						return assetMapper.map(response.data, assetCommonConfig.assetServiceBaseUri);
					})
					.catch(function (response) {
						return $q.reject(httpUtility.getErrorReason(response));
					});
			}

			function getAssetsAsync(assetIds, cancellationToken) {
				var request = { items: _.map(assetIds, function (id) { return { id: id }; }) };
				return $http.post(uriUtility.fromPattern(assetCommonConfig.assetServiceBaseUri + 'assets/get', { fields: assetsFieldFilter }), request, { timeout: cancellationToken })
					.then(function (response) {
						return {
							assets: _.map(response.data.items, function (item) { return assetMapper.map(item, assetCommonConfig.assetServiceBaseUri); })
						};
					})
					.catch(function (response) {
						return $q.reject(httpUtility.getErrorReason(response));
					});
			}

			function getFileLinksForAssetsAsync(assetFileIds, cancellationToken) {
				return $http.post(assetCommonConfig.assetServiceBaseUri + 'assets/files/links/get', { items: assetFileIds }, { timeout: cancellationToken })
					.then(function (response) {
						return response && response.data && response.data.items;
					})
					.catch(function (response) {
						return $q.reject(httpUtility.getErrorReason(response));
					});
			}

			function getRevisionsAsync(id, revisions, next) {
				revisions = revisions || [];
				return $http.get(getAssetRevisionsUri(id, { limit: revisionRequestLimit, next: next }))
					.then(function (response) {
						var newerRevision = revisions.length === 0 ? null : revisions[revisions.length - 1];
						_.forEach(response.data.items, function (revision) {
							if (newerRevision && newerRevision.agent && newerRevision.agent.user && revision.agent && revision.agent.user &&
								newerRevision.date && revision.date &&
								newerRevision.changes && newerRevision.changes.ops && revision.changes && revision.changes.ops &&
								newerRevision.agent.user.id === revision.agent.user.id &&
								dateTimeUtility.differenceInSeconds(newerRevision.date, revision.date) <= mergeWithinMinutes * 60) {
								// merge operations of revisions of the same user near the same time
								newerRevision.changes.ops = revision.changes.ops.concat(newerRevision.changes.ops);
							}
							else {
								revisions.push(revision);
								newerRevision = revision;
							}
						});

						// get more revisions if necessary
						if (response.data.next)
							return getRevisionsAsync(id, revisions, response.data.next);
						else
							return revisionMapper.map(revisions);
					});
			}

			function searchAssetsAsync(options, cancellationToken) {
				options = _.extend({}, options, { bucket: bucketService.getCurrentBucket().id });

				return $http.get(uriUtility.fromPattern(assetCommonConfig.assetServiceBaseUri + 'assets/search', options), { timeout: cancellationToken })
					.then(function (response) {
						return {
							hits: _(response.data.hits).pluck('asset').map(function (item) { return assetMapper.map(item, assetCommonConfig.assetServiceBaseUri); }).value(),
							total: response.data.hitTotal,
							facets: facetMapper.map(response.data.facets)
						};
					})
					.catch(function (response) {
						return $q.reject(httpUtility.getErrorReason(response));
					});
			}

			function createAssetAsync(uploadedFile, operations, options) {
				var request = {
					bucket: bucketService.getCurrentBucket().id,
					ops: operations,
					waitForIndexing: true
				};

				return $http.post(assetCommonConfig.assetServiceBaseUri + 'assets/create', request, { timeout: options && options.cancellationToken })
					.then(function (response) {
						return response.data;
					})
					.catch(function () {
						return $q.reject('Error creating asset.');
					});
			}

			function revertAssetToRevisionAsync(assetId, revertTo) {
				return editAssetsAsync([assetId], [{ op: 'revertAsset', revisionId: revertTo }], { shouldAwaitCompletion: true });
			}

			function reportAssetView(assetId, share) {
				return $http.post(uriUtility.fromPattern(assetCommonConfig.assetServiceBaseUri + 'assets/{id}/viewed', { id: assetId, share: share }))
					.then(function () {
						return null;
					})
					.catch(function () { });
			}

			function reportAssetDownload(assetId, fileId, share) {
				return $http.post(uriUtility.fromPattern(assetCommonConfig.assetServiceBaseUri + 'assets/{id}/files/{fileId}/downloaded', { id: assetId, fileId: fileId, share: share }))
					.then(function () {
						return null;
					})
					.catch(function () { });
			}

			var lastSaveStartDate = null;
			var lastSaveEndDate = null;
			function getLastSaveDate() {
				return {
					lastSaveStartDate: lastSaveStartDate,
					lastSaveEndDate: lastSaveEndDate
				};
			}

			function editAssetsAsync(assetIds, operations, options, shareToken) {
				lastSaveStartDate = Date.now();

				options = options || {};
				var request = {
					assetIds: assetIds,
					ops: operations
				};

				var params = {};
				if (shareToken)
					params.share = shareToken;

				if (options.fireAndForget)
					params.fields = '!items';
				else
					params.fields = 'items.asset.(id,revision)';

				return $http.post(uriUtility.fromPattern(assetCommonConfig.assetServiceBaseUri + 'assets/edit', params), request, { timeout: options.cancellationToken })
					.then(function (response) {

						if (options.fireAndForget)
							return null;

						if (response.status === 202) {
							if (options.shouldAwaitCompletion) {
								return jobService.awaitBulkJobCompletionAsync([response.data.id], {
									cancellationToken: options.cancellationToken,
									responseFormatter: function (result) {
										return assetMapper.map(result, assetCommonConfig.assetServiceBaseUri);
									}
								});
							}
							else {
								return response.data;
							}
						}
						else {
							return response.data.items.map(function (x) { return assetMapper.map(x.asset, assetCommonConfig.assetServiceBaseUri); });
						}
					})
					.finally(function () {
						lastSaveEndDate = Date.now();
					});
			}

			return {
				getAssetUri: getAssetUri,
				getAssetExifToolAsync: getAssetExifToolAsync,
				addToFavoritesAsync: addToFavoritesAsync,
				removeFromFavoritesAsync: removeFromFavoritesAsync,
				addToBoardAsync: addToBoardAsync,
				removeFromBoardAsync: removeFromBoardAsync,
				getAssetRevisionsUri: getAssetRevisionsUri,
				getAssetAsync: getAssetAsync,
				getAssetsAsync: getAssetsAsync,
				getFileLinksForAssetsAsync: getFileLinksForAssetsAsync,
				getRevisionsAsync: getRevisionsAsync,
				searchAssetsAsync: searchAssetsAsync,
				createAssetAsync: createAssetAsync,
				updateAssetAsync: updateAssetAsync,
				revertAssetToRevisionAsync: revertAssetToRevisionAsync,
				deleteAssetsAsync: deleteAssetsAsync,
				undeleteAssetsAsync: undeleteAssetsAsync,
				createShareAsync: createShareAsync,
				getShareAssetIdAsync: getShareAssetIdAsync,
				reportAssetView: reportAssetView,
				reportAssetDownload: reportAssetDownload,
				editAssetsAsync: editAssetsAsync,
				getLastSaveDate: getLastSaveDate
			};
		}
		assetService.$inject = ['$http', '$q', 'fileUploadService', 'bucketService', 'cancellationService', 'jobService', 'promiseUtilityService', '_', 'assetMapper', 'revisionMapper', 'shareMapper', 'facetMapper', 'dateTimeUtility', 'utility', 'uriUtility', 'httpUtility', 'assetCommonConfig'];
})();
